import { UserCheckIcon, XIcon } from './components/Icons';
import { useBodyScrollLock } from './hooks/useBodyScrollLock';

import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import { useClient } from './contexts/ClientContext'; 
import { ChatLayoutProvider, useChatLayout } from './contexts/ChatLayoutContext';
import Header from './components/Header';
import MobileBottomNav from './components/MobileBottomNav';
import MobileMenuPage from './components/MobileMenuPage';
import MessagesPage from './components/MessagesPage';
import ClientProfilePage from './components/ClientProfilePage';
import ResourcesPage from './components/ResourcesPage';
import Hero from './components/Hero';
import Horoscope from './components/Horoscope';
import CrystalBall from './components/CrystalBall';
import LoveCompatibility from './components/LoveCompatibility';
import Numerology from './components/Numerology';
import DreamInterpretation from './components/DreamInterpretation';
import TarotOracle from './components/TarotOracle';
import Services from './components/Services';
import Footer from './components/Footer';
import LiveChat from './components/LiveChat';
import MediumsList from './components/MediumsList';
import LiveActivityFeed from './components/LiveActivityFeed'; 
import Home from './components/Home';
import MysticBackground from './components/MysticBackground';
import CookieConsent from './components/CookieConsent';
import IOSPwaPrompt from './components/IOSPwaPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import PromoNotificationWidget from './components/PromoNotificationWidget';
import { LiveVoiceCallModal } from './components/LiveVoiceCallModal';
import FloatingCallWidget from './components/FloatingCallWidget';
import MediumProfileModal from './components/MediumProfileModal';
import PremiumModal from './components/PremiumModal';
import AdminLogin from './components/AdminLogin';
import LegalModal from './components/LegalModal';
import ClientAuthModal from './components/ClientAuthModal';
import ClientProfileModal from './components/ClientProfileModal';
import MandatoryLeadModal from './components/MandatoryLeadModal';
import NightSupportChat from './components/NightSupportChat';
import AdminPurchaseChat from './components/AdminPurchaseChat';
import Forum from './components/Forum';
import { Medium, UserLocation, GuestUser } from './types';
import { socket } from './services/socket';
import { getIpInfo } from './services/ipService';
import { ALL_MEDIUMS, SARAH_MEDIUM, isMediumVip } from './data/mediums';
import { initGlobalSmoothScroll, destroyGlobalSmoothScroll } from './services/smoothScroll';

// Dashboard d'administration lourd chargé dynamiquement avec auto-rechargement propre en cas de nouvelle version
const lazyWithRetry = (componentImport: () => Promise<any>) =>
    lazy(async () => {
        const hasBeenForceRefreshed = typeof window !== 'undefined' && JSON.parse(
            window.sessionStorage.getItem('admin_chunk_reload') || 'false'
        );

        try {
            const component = await componentImport();
            if (typeof window !== 'undefined') {
                window.sessionStorage.setItem('admin_chunk_reload', 'false');
            }
            return component;
        } catch (error) {
            if (typeof window !== 'undefined' && !hasBeenForceRefreshed) {
                // Nouvelle version déployée sur le serveur -> recharger la page pour récupérer les nouveaux bundles
                window.sessionStorage.setItem('admin_chunk_reload', 'true');
                window.location.reload();
                return new Promise(() => {});
            }
            throw error;
        }
    });

const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard'));


// --- CALCULATEUR DE PAGE INITIALE DEPUIS L'URL / HASH / PATHNAME ---
const pageAliases: Record<string, string> = {
    'tarot': 'tarot', 'oracle': 'tarot', 'tirage': 'tarot', 'cartes': 'tarot',
    'horoscope': 'horoscope', 'astrologie': 'horoscope', 'zodiaque': 'horoscope',
    'love': 'love', 'love-match': 'love', 'amour': 'love', 'match': 'love', 'compatibilite': 'love',
    'dreams': 'dreams', 'reves': 'dreams', 'explication-reves': 'dreams',
    'numerology': 'numerology', 'numerologie': 'numerology', 'chemin-de-vie': 'numerology',
    'crystal': 'crystal', 'boule': 'crystal', 'cristal': 'crystal', 'boule-de-cristal': 'crystal',
    'mediums': 'mediums', 'voyants': 'mediums', 'nos-mediums': 'mediums',
    'tarifs': 'tarifs', 'services': 'tarifs', 'pricing': 'tarifs', 'contact': 'tarifs', 'forfaits': 'tarifs',
    'messages': 'messages', 'messagerie': 'messages', 'inbox': 'messages',
    'profile': 'profile', 'espace-client': 'profile', 'mon-compte': 'profile', 'compte': 'profile', 'account': 'profile',
    'menu': 'menu',
    'ressources': 'ressources', 'outils': 'ressources',
    'home': 'home', 'accueil': 'home'
};

const getInitialActivePage = (): string => {
    if (typeof window === 'undefined') return 'home';
    const hash = (window.location.hash || '').toLowerCase().replace(/^#\/?/, '');
    const pathname = (window.location.pathname || '').toLowerCase().replace(/^\//, '').replace(/\.html$/, '');
    const search = (window.location.search || '').toLowerCase();

    // Vérifier forum en priorité absolue (avec support des sous-liens de sujets)
    if (hash === 'forum' || hash.startsWith('forum-') || hash.startsWith('forum/') || pathname === 'forum' || pathname.startsWith('forum/') || search.includes('page=forum')) {
        return 'forum';
    }

    if (pageAliases[hash]) return pageAliases[hash];
    if (pageAliases[pathname]) return pageAliases[pathname];

    for (const [alias, page] of Object.entries(pageAliases)) {
        if (search.includes(`page=${alias}`)) return page;
    }
    
    return 'home';
};

const AppContent = () => {
  const { isAuthenticated, logout, logVisit, globalMediumStatus, isMaintenanceMode, isLeadModalEnabled, sessions, sessionsHistory } = useAdmin(); 
  const { client, isAuthModalOpen, openAuthModal, quickRegisterClient } = useClient(); 
  const { chatSessions, openChat, closeChat } = useChatLayout();

  const [currentPage, setCurrentPage] = useState(getInitialActivePage);

  // Calcul du nombre de messages non lus pour le client connecté
  const unreadMessagesCount = useMemo(() => {
      if (!client) return 0;
      const unreadAdmin = (client.adminMessages || []).filter(m => !m.read).length;
      const allUserSessions = [...(sessions || []), ...(sessionsHistory || [])];
      const clientSessions = allUserSessions.filter(s => 
          (client.phone && s.clientPhone === client.phone) || 
          (client.id && s.clientId === client.id)
      );
      const unreadSessionsCount = clientSessions.reduce((acc, s) => {
          if (s.hasUnreadClient) return acc + 1;
          return acc;
      }, 0);
      return unreadAdmin + unreadSessionsCount;
  }, [client, sessions, sessionsHistory]);

  // État de validation de l'accès client (enregistré avec mot de passe ou connecté)
  const [isLeadDone, setIsLeadDone] = useState<boolean>(() => {
      if (typeof window === 'undefined') return false;
      try {
          return Boolean(localStorage.getItem('astro_user_registered_v2'));
      } catch {
          return false;
      }
  });

  // Ne pas bloquer UNIQUEMENT si l'URL pointe explicitement sur la page d'administration
  const isDirectAdminUrl = typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/admin') ||
      window.location.hash.startsWith('#admin') ||
      currentPage === 'admin'
  );

  // LA POPUP OBLIGATOIRE BLOQUE TOUT L'ÉCRAN À L'OUVERTURE DU SITE POUR TOUT VISITEUR NON INSCRIT
  const isLeadModalVisible = Boolean(isLeadModalEnabled) && !isLeadDone && !isDirectAdminUrl;
  
  const [profileMedium, setProfileMedium] = useState<Medium | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  const [guestUser, setGuestUser] = useState<GuestUser | null>(null);
  const [pendingChatMedium, setPendingChatMedium] = useState<Medium | null>(null);
  const [initialMessageMedium, setInitialMessageMedium] = useState<Medium | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | undefined>(undefined);
  
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showPurchaseChat, setShowPurchaseChat] = useState(false);
  const [purchasePack, setPurchasePack] = useState<'50' | '100' | '150' | null>(null);
  const [pendingPurchasePack, setPendingPurchasePack] = useState<'50' | '100' | '150' | null>(null);
  const [pendingVipMedium, setPendingVipMedium] = useState<Medium | null>(null);

  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  
  const [isNightMode, setIsNightMode] = useState(false);
  const [showNightChat, setShowNightChat] = useState(false);

  const [activePopup, setActivePopup] = useState<'promo' | 'pack' | null>(null);
  const [hasUnreadSarahBubble, setHasUnreadSarahBubble] = useState(false);
  const [isSarahFloatingDismissed, setIsSarahFloatingDismissed] = useState(false);

  const [hasCookieConsent, setHasCookieConsent] = useState(() => {
      if (typeof window === 'undefined') return true;
      return Boolean(localStorage.getItem('astro_cookie_consent'));
  });

  useEffect(() => {
      const handleConsentUpdate = () => {
          setHasCookieConsent(Boolean(localStorage.getItem('astro_cookie_consent')));
      };
      window.addEventListener('astro_cookie_consent_updated', handleConsentUpdate);
      window.addEventListener('storage', handleConsentUpdate);
      return () => {
          window.removeEventListener('astro_cookie_consent_updated', handleConsentUpdate);
          window.removeEventListener('storage', handleConsentUpdate);
      };
  }, []);

  // --- FONCTION DE NAVIGATION GLOBALE PUSHSTATE SANS RECHARGEMENT ---
  const navigateTo = (pathOrPage: string, pushState = true) => {
      if (typeof window === 'undefined') return;
      let cleanPath = pathOrPage.trim();
      if (!cleanPath.startsWith('/') && !cleanPath.startsWith('#') && !cleanPath.startsWith('http')) {
          cleanPath = `/${cleanPath === 'home' ? '' : cleanPath}`;
      }
      if (cleanPath === '/home') cleanPath = '/';
      if (pushState && (window.location.pathname + window.location.hash) !== cleanPath) {
          window.history.pushState({}, '', cleanPath || '/');
      }
      executeRouting(cleanPath);
  };

  // Exposer la fonction de navigation globale sur l'objet window
  useEffect(() => {
      if (typeof window !== 'undefined') {
          (window as any).astroNavigate = (path: string) => navigateTo(path, true);
      }
  }, []);

  // --- VERROUILLAGE AUTOMATIQUE DU SCROLL DU FOND UNIQUEMENT POUR LES VRAIES MODALES PLEIN ÉCRAN ---
  const isAnyModalOpen = Boolean(
    showPremiumModal ||
    showLegalModal ||
    showProfileModal ||
    showAdminLogin ||
    isAuthModalOpen ||
    profileMedium
  );
  useBodyScrollLock(isAnyModalOpen);

  // --- ROUTEUR UNIFIÉ ROBUSTE (HASH, PATHNAME, QUERY, PUSH NOTIFICATIONS) ---
  const executeRouting = (explicitUrl?: string) => {
      if (typeof window === 'undefined') return;
      const currentHref = (explicitUrl || window.location.href).toLowerCase();
      const hash = (window.location.hash || '').toLowerCase().replace(/^#\/?/, '');
      const pathname = (window.location.pathname || '').toLowerCase().replace(/^\//, '');
      const search = (window.location.search || '').toLowerCase();

      console.log('[ROUTER] Traitement URL:', { currentHref, hash, pathname, search });

      // 1. Accès Admin & Agent (/admin, /admin/dashboard, /admin/chats, /agent, /agent/chats, /login, /dashboard)
      if (pathname.startsWith('admin') || hash.startsWith('admin') || pathname.startsWith('agent') || hash.startsWith('agent') || hash === 'login' || pathname === 'login' || search.includes('admin') || search.includes('login') || search.includes('agent')) {
          setShowAdminLogin(true);
          return;
      }

      // 2. Accès Mentions Légales & CGU (/legal, /cgu, /mentions, /confidentialite, /rgpd)
      if (hash === 'legal' || hash === 'cgu' || hash === 'mentions' || hash === 'confidentialite' || pathname === 'legal' || pathname === 'cgu' || pathname === 'mentions' || pathname === 'confidentialite' || search.includes('legal') || search.includes('cgu')) {
          setShowLegalModal(true);
          return;
      }

      // 3. Tarifs & Recharge Astres (/premium, /recharge, /packs, /forfaits, /acheter-astres)
      if (hash === 'premium' || hash === 'recharge' || hash === 'packs' || pathname === 'premium' || pathname === 'recharge' || pathname === 'packs' || search.includes('premium') || search.includes('recharge')) {
          setShowPremiumModal(true);
          return;
      }

      // 4. Chat Secrétariat Sarah (/sarah, /support, /assistance, /secretariat, /chat-9999)
      if (hash === 'sarah' || hash === 'support' || hash === 'assistance' || hash === 'chat-9999' || pathname === 'sarah' || pathname === 'support' || pathname === 'assistance' || search.includes('sarah') || search.includes('chat=9999')) {
          setPurchasePack(null);
          setPendingVipMedium(null);
          setShowPurchaseChat(true);
          return;
      }

      // 5. Espace Client / Mon Compte / Connexion / Inscription (/client, /client/profile, /profile, /compte, /mon-compte, /connexion, /inscription)
      if (pathname.startsWith('client/login') || hash === 'connexion' || pathname === 'connexion' || search.includes('login=client')) {
          openAuthModal();
          return;
      }
      if (pathname.startsWith('client') || hash === 'profile' || hash === 'mon-compte' || hash === 'compte' || hash === 'historique' || pathname === 'profile' || pathname === 'compte' || pathname === 'mon-compte' || pathname === 'historique' || search.includes('profile')) {
          setShowProfileModal(true);
          return;
      }

      // 6. Messagerie Privée : STRICTEMENT INTERDITE AUX VISITEURS NON INSCRITS
      if (currentHref.includes('/messages') || pathname.startsWith('messages') || hash.startsWith('messages')) {
          const storedClient = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('astro_current_client') || 'null') : null;
          const isAdmin = typeof window !== 'undefined' ? Boolean(localStorage.getItem('admin_logged_user')) : false;

          if (!storedClient && !isAdmin) {
              console.warn("[SÉCURITÉ] Accès interdit à la messagerie sans compte client. Redirection.");
              openAuthModal();
              setCurrentPage('home');
              if (typeof window !== 'undefined') {
                  window.history.replaceState(null, '', '/');
              }
              return;
          }

          // Visiteur connecté : Détection du médium demandé
          const clientChatMatch = currentHref.match(/messages\/(?:id[=-])?([a-zA-Z0-9_-]+)(?:\/(?:chat(\d+)|(sarah)))?/i) || 
                                  currentHref.match(/[#/?]messages[#/?]id=([a-zA-Z0-9_-]+)(?:[&/?](?:chat=(\d+)|(sarah)))?/i);

          if (clientChatMatch) {
              const extractedClientId = clientChatMatch[1];
              const extractedMediumId = clientChatMatch[2] ? parseInt(clientChatMatch[2], 10) : null;
              const isSarahRoute = clientChatMatch[3] === 'sarah' || currentHref.includes('/sarah');

              if (isSarahRoute) {
                  setInitialMessageMedium(SARAH_MEDIUM);
              } else if (extractedMediumId) {
                  const targetMed = ALL_MEDIUMS.find(m => m.id === extractedMediumId);
                  if (targetMed) {
                      setInitialMessageMedium(targetMed);
                  }
              }
          }
          setCurrentPage('messages');
          return;
      }

      // Lancement direct d'un chat médium (#chat-1, #chat-2, /chat/1, /chat/12, /consultation/12, ?chat=1, ?medium=1)
      const chatMatch = currentHref.match(/[#/?](?:chat|consultation)[-=/](\d+)/);
      if (chatMatch) {
          const medId = parseInt(chatMatch[1], 10);
          if (medId === 9999) {
              setShowPurchaseChat(true);
          } else {
              const targetMed = ALL_MEDIUMS.find(m => m.id === medId);
              if (targetMed) {
                  setInitialMessageMedium(targetMed);
                  setCurrentPage('messages');
              }
          }
          return;
      }

      // 7. Fiche profil d'un médium (#profile-1, #medium-1, /medium/1, /mediums/1, /voyant/1, /profil-medium/1, ?medium=1)
      const mediumMatch = currentHref.match(/[#/?](?:profile|medium|mediums|voyant|voyants|profil-medium)[-=/](\d+)/);
      if (mediumMatch) {
          const medId = parseInt(mediumMatch[1], 10);
          const targetMed = ALL_MEDIUMS.find(m => m.id === medId);
          if (targetMed) {
              setProfileMedium(targetMed);
          }
          return;
      }

      // 8. Forum & Discussions direct (#forum, #forum-xyz, /forum, /forum/xyz)
      if (hash === 'forum' || hash.startsWith('forum-') || hash.startsWith('forum/') || pathname === 'forum' || pathname.startsWith('forum/') || search.includes('page=forum')) {
          setCurrentPage('forum');
          return;
      }

      // 9. Navigation directe vers une page (Tarot, Horoscope, Love, etc.)
      const matchedPage = pageAliases[hash] || pageAliases[pathname];
      if (matchedPage) {
          setCurrentPage(matchedPage);
          return;
      }

      for (const [alias, page] of Object.entries(pageAliases)) {
          if (search.includes(`page=${alias}`)) {
              setCurrentPage(page);
              return;
          }
      }

      // Par défaut si URL racine (/) ou chemin d'accueil
      if (!hash && (!pathname || pathname === '' || pathname === 'home' || pathname === 'index' || pathname === 'index.html')) {
          setCurrentPage('home');
          return;
      }
  };

  useEffect(() => {
      // Exécution immédiate au montage
      executeRouting();

      // Écoute de l'événement personnalisé pour ouvrir les mentions légales & CGU
      const handleOpenLegalEvent = () => {
          setShowLegalModal(true);
          navigateTo('/legal', true);
      };
      window.addEventListener('astro_open_legal', handleOpenLegalEvent);

      // Écoute des changements d'URL
      window.addEventListener('hashchange', () => executeRouting());
      window.addEventListener('popstate', () => executeRouting());

      // Écoute des messages envoyés par le Service Worker lors du clic sur un Push
      const swMessageHandler = (event: MessageEvent) => {
          if (event.data && event.data.type === 'ASTRO_NOTIFICATION_CLICK') {
              console.log('[PUSH CLICK MESSAGE REÇU DU SERVICE WORKER]:', event.data);
              const targetUrl = event.data.url || event.data.fullUrl;
              if (targetUrl) {
                  navigateTo(targetUrl, true);
              }
          }
      };

      if ('serviceWorker' in navigator) {
          navigator.serviceWorker.addEventListener('message', swMessageHandler);
      }

      return () => {
          window.removeEventListener('astro_open_legal', handleOpenLegalEvent);
          window.removeEventListener('hashchange', () => executeRouting());
          window.removeEventListener('popstate', () => executeRouting());
          if ('serviceWorker' in navigator) {
              navigator.serviceWorker.removeEventListener('message', swMessageHandler);
          }
      };
  }, [openChat]);


  // --- INITIALISATION DU MOTEUR DE DÉFILEMENT FLUIDE (SMOOTH SCROLL) ---
  useEffect(() => {
      const scrollController = initGlobalSmoothScroll();
      return () => {
          destroyGlobalSmoothScroll();
      };
  }, []);

  // Défilement instantané vers le haut lors d'un changement de page
  useEffect(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [currentPage]);

  // --- NOTIFICATION SONORE ET VISUELLE EN TEMPS RÉEL POUR LE CLIENT ---
  const [adminMessageNotif, setAdminMessageNotif] = useState<{
      id: string;
      sessionId: string;
      mediumId?: number;
      senderName: string;
      text: string;
  } | null>(null);

  const playClientNotificationChime = () => {
      try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (!AudioContextClass) return;
          const ctx = new AudioContextClass();
          const now = ctx.currentTime;
          
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc1.type = 'sine';
          osc2.type = 'sine';
          
          osc1.frequency.setValueAtTime(587.33, now); // D5
          osc2.frequency.setValueAtTime(880, now + 0.12); // A5
          
          gain.gain.setValueAtTime(0.25, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
          
          osc1.connect(gain);
          osc2.connect(gain);
          gain.connect(ctx.destination);
          
          osc1.start(now);
          osc1.stop(now + 0.12);
          osc2.start(now + 0.12);
          osc2.stop(now + 0.5);
      } catch (e) {
          console.error("Audio notification chime error:", e);
      }
  };

  // Ref pour suivre les sessions déjà notifiées (afficher la bannière UNE SEULE FOIS au début de la conversation)
  const notifiedSessionsRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
      const handleReceiveMessage = (data: { sessionId: string; message: any }) => {
          if (!data || !data.message || isAuthenticated) return;
          const { sessionId, message } = data;

          if (message.role === 'model') {
              const currentClientStored = typeof window !== 'undefined' ? localStorage.getItem('astro_current_client') : null;
              const parsedClient = currentClientStored ? JSON.parse(currentClientStored) : null;
              
              const activeClientId = client?.id || parsedClient?.id;
              const activeClientPhone = (client?.phone || parsedClient?.phone || '').replace(/\s+/g, '');
              const activeClientName = (client?.name || parsedClient?.name || '').trim().toLowerCase().replace(/\s+/g, '_');
              const guestSessionId = typeof window !== 'undefined' ? localStorage.getItem('sarah_guest_session_id') : null;

              const isForMe = (activeClientId && sessionId.includes(activeClientId)) ||
                              (activeClientPhone && sessionId.includes(activeClientPhone)) ||
                              (activeClientName && sessionId.includes(activeClientName)) ||
                              (guestSessionId && sessionId === guestSessionId) ||
                              sessionId.includes('guest') ||
                              sessionId.startsWith('sarah-');

              if (isForMe) {
                  const isSarahMsg = (
                      message.mediumId === 9999 || 
                      sessionId.includes('m9999') || 
                      sessionId.startsWith('sarah-')
                  );

                  // 1. Jouer le son de notification
                  playClientNotificationChime();

                  if (isSarahMsg) {
                      // ISOLATION STRICTE DE SARAH (9999) :
                      // Activer la pastille discrète sur la bulle sans bannière intrusive
                      setHasUnreadSarahBubble(true);
                  } else {
                      // ISOLATION STRICTE DES MÉDIUMS (ex: Maud K., Bruno G., Clara X.) :
                      // Ne JAMAIS ouvrir la fenêtre de Sarah ! Ouvrir uniquement la bulle/fenêtre du médium concerné !
                      let targetMedium = ALL_MEDIUMS.find(m => m.id === message.mediumId);
                      if (!targetMedium && message.mediumName) {
                          targetMedium = ALL_MEDIUMS.find(m => (m.name || '').toLowerCase() === (message.mediumName || '').toLowerCase());
                      }
                      if (targetMedium) {
                          openChat(targetMedium);
                      }
                  }
              }
          }
      };

      socket.on('receive_message', handleReceiveMessage);
      socket.on('new_message', (msgData: any) => {
          if (msgData && msgData.sessionId) {
              handleReceiveMessage(msgData);
          }
      });

      return () => {
          socket.off('receive_message', handleReceiveMessage);
          socket.off('new_message');
      };
  }, [client, openChat]);

  // Déclenchement de l'achat d'un pack guidé par l'Admin
  const handleOpenPurchase = (pack: '50' | '100' | '150' = '100') => {
      setPurchasePack(pack);
      setPendingVipMedium(null);
      setShowPremiumModal(false);
      setActivePopup(null);
      if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('astro_close_popups'));
          window.dispatchEvent(new CustomEvent('astro_switch_purchase_pack', { detail: { pack } }));
      }
      setIsSarahFloatingDismissed(false);
      setShowPurchaseChat(true);
  };

  // Écouteur pour fermer les popups publicitaires à l'ouverture d'un chat
  useEffect(() => {
      const closePopupsHandler = () => {
          setActivePopup(null);
          setShowPremiumModal(false);
      };
      window.addEventListener('astro_close_popups', closePopupsHandler);
      return () => window.removeEventListener('astro_close_popups', closePopupsHandler);
  }, []);

  // Écouteur d'évènement global pour ouvrir l'assistance achat
  useEffect(() => {
      const handler = (e: Event) => {
          const detail = (e as CustomEvent).detail;
          const pack = (['50', '100', '150'].includes(detail?.pack)) ? detail.pack : '100';
          handleOpenPurchase(pack);
      };
      window.addEventListener('astro_open_purchase_chat', handler);
      return () => window.removeEventListener('astro_open_purchase_chat', handler);
  }, [client]);

  // Si le client vient de s'inscrire ou se connecter après avoir sélectionné un voyant
  useEffect(() => {
      if (client && pendingChatMedium) {
          const isVip = pendingChatMedium.isVip !== undefined ? Boolean(pendingChatMedium.isVip) : isMediumVip(pendingChatMedium);
          const clientPoints = client.loyaltyPoints || 0;
          if (isVip && clientPoints < 1000) {
              setPendingVipMedium(pendingChatMedium);
              setPurchasePack('100');
              setShowPurchaseChat(true);
              setPendingChatMedium(null);
              return;
          }
          setInitialMessageMedium(pendingChatMedium);
          navigateTo(`/messages/id=${client.id}/chat${pendingChatMedium.id}`, true);
          setPendingChatMedium(null);
      }
  }, [client, pendingChatMedium]);

  // Si le client vient de se connecter après avoir cliqué sur un pack
  useEffect(() => {
      if (client && pendingPurchasePack) {
          setPurchasePack(pendingPurchasePack);
          setShowPurchaseChat(true);
          setPendingPurchasePack(null);
      }
  }, [client, pendingPurchasePack]);

  // Raccourci clavier secret d'accès Admin : Ctrl + Shift + Q (ou Cmd + Shift + Q sur Mac)
  // + Support accès mobile direct via URL (?admin ou #admin)
  useEffect(() => {
      const checkUrlForAdmin = () => {
          if (typeof window !== 'undefined') {
              const urlParams = new URLSearchParams(window.location.search);
              if (urlParams.has('admin') || window.location.hash.includes('admin')) {
                  setShowAdminLogin(true);
              }
          }
      };
      checkUrlForAdmin();
      window.addEventListener('hashchange', checkUrlForAdmin);

      const handleKeyDown = (e: KeyboardEvent) => {
          if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'Q' || e.key === 'q' || e.code === 'KeyQ')) {
              e.preventDefault();
              setShowAdminLogin(prev => !prev);
          }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
          window.removeEventListener('keydown', handleKeyDown);
          window.removeEventListener('hashchange', checkUrlForAdmin);
      };
  }, []);

  useEffect(() => {
      const checkTime = () => {
          const now = new Date();
          const h = now.getHours();
          const day = now.getDay(); // 0 = Dimanche, 6 = Samedi
          
          // Règle : Fermé le Samedi (toute la journée) OU Fermé la nuit (23h - 08h)
          const isClosed = day === 6 || h >= 23 || h < 8;
          
          setIsNightMode(isClosed);
      };
      checkTime();
      const interval = setInterval(checkTime, 60000);
      return () => clearInterval(interval);
  }, []);

  useEffect(() => {
      // SI MODE MAINTENANCE : PAS DE POPUPS
      if (isMaintenanceMode) return;

      const promoTimer = setTimeout(() => {
          const lastShownStr = localStorage.getItem('astro_promo_last_shown');
          const now = Date.now();
          const COOLDOWN = 15 * 60 * 1000; 

          let shouldShow = false;
          if (!lastShownStr) shouldShow = true;
          else if (now - parseInt(lastShownStr, 10) > COOLDOWN) shouldShow = true;

          if (shouldShow && !activePopup) { 
              setActivePopup('promo');
              localStorage.setItem('astro_promo_last_shown', now.toString());
          }
      }, 30000);

      const packTimer = setTimeout(() => {
          const lastShownPack = localStorage.getItem('astro_pack_popup_time');
          const now = Date.now();
          const COOLDOWN_PACK = 300000; 

          let shouldShowPack = false;
          if (!lastShownPack) shouldShowPack = true;
          else if (now - parseInt(lastShownPack, 10) > COOLDOWN_PACK) shouldShowPack = true;

          if (shouldShowPack) {
              setActivePopup((current) => current === null ? 'pack' : current); 
              if (activePopup === null) { 
                  localStorage.setItem('astro_pack_popup_time', now.toString());
              }
          }
      }, 120000);

      return () => {
          clearTimeout(promoTimer);
          clearTimeout(packTimer);
      };
  }, [isMaintenanceMode]); 

  useEffect(() => {
      const initLocation = async () => {
          const loc = await getIpInfo();
          if (loc) {
              const uLoc = {
                  ip: loc.ip,
                  country: loc.country,
                  country_code: loc.country_code,
                  city: loc.city,
                  flag: loc.flag
              };
              setUserLocation(uLoc);
              const hasLoggedVisit = sessionStorage.getItem('astro_visit_logged');
              if (!hasLoggedVisit) {
                  logVisit(uLoc); 
                  sessionStorage.setItem('astro_visit_logged', 'true');
              }
          }
      };
      initLocation();
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem('astro_cookie_consent');
    if (consent === 'true') {
        const savedUser = localStorage.getItem('astro_guest_user');
        if (savedUser) {
            try {
                setGuestUser(JSON.parse(savedUser));
            } catch (e) {
                console.error("Erreur lecture cookie user", e);
            }
        }
    }
  }, []);

  useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // --- INVITATION AUTOMATIQUE DE SARAH (SECRÉTARIAT) APRÈS 25 SECONDES POUR LES NOUVEAUX VISITEURS ---
  useEffect(() => {
      if (typeof window === 'undefined') return;
      const alreadyWelcomed = sessionStorage.getItem('astro_sarah_welcomed_25s');
      if (alreadyWelcomed || client) return;

      const secretaryTimer = setTimeout(() => {
          if (!client && !showPurchaseChat) {
              sessionStorage.setItem('astro_sarah_welcomed_25s', 'true');
              setHasUnreadSarahBubble(true);
          }
      }, 25000);

      return () => clearTimeout(secretaryTimer);
  }, [client, showPurchaseChat]);

  const handleChatStart = (medium: Medium) => {
      // 1. Sarah (Secrétariat ID 9999) : OUVERTE À TOUT LE MONDE SANS INSCRIPTION (Clients et Visiteurs)
      if (medium.id === 9999 || medium.name?.toLowerCase().includes('sarah')) {
          setProfileMedium(null);
          setIsSarahFloatingDismissed(false);
          setPurchasePack(null);
          setPendingVipMedium(null);
          setShowPurchaseChat(true);
          return;
      }

      const isForcedOffline = globalMediumStatus === 'force_offline';
      
      if (isForcedOffline || (isNightMode && medium.status !== 'available')) {
          setProfileMedium(null); 
          setShowNightChat(true);
          navigateTo('/night-chat', true);
          return;
      }

      // 2. Vérification VIP (+1000 Astres requis)
      const isVip = medium.isVip !== undefined ? Boolean(medium.isVip) : isMediumVip(medium);
      const clientPoints = client?.loyaltyPoints || 0;
      if (isVip && (!client || clientPoints < 1000)) {
          setProfileMedium(null);
          if (!client) {
              setPendingChatMedium(medium);
              openAuthModal('register');
              return;
          }
          // Client connecté avec moins de 1000 Astres : ouvrir directement le secrétariat Sarah pour l'offre VIP Pack 100€
          setPendingVipMedium(medium);
          setPurchasePack('100');
          setShowPurchaseChat(true);
          return;
      }

      setProfileMedium(null);
      setInitialMessageMedium(medium);

      // Client connecté : accès direct à son salon privé
      if (client) {
          navigateTo(`/messages/id=${client.id}/chat${medium.id}`, true);
          return;
      }

      // Visiteur non inscrit : EXIGER L'INSCRIPTION COMPLÈTE OFFICIELLE (PAS DE COMPTE INVITÉ)
      setPendingChatMedium(medium);
      openAuthModal('register');
  };
  
  const handleCloseSession = (mediumId: number) => {
      closeChat(mediumId);
      navigateTo(`/${currentPage === 'home' ? '' : currentPage}`, false);
  };

  const handleProfileClick = (medium: Medium) => {
      setProfileMedium(medium);
      navigateTo(`/medium/${medium.id}`, true);
  };

  const handleNavigate = (page: string, category: string | null = null) => {
      setSelectedCategory(category);
      const targetPage = (page === 'home' || !page) ? 'home' : page;

      // SÉCURITÉ : La page messages requiert obligatoirement une session client ou admin
      if (targetPage === 'messages' && !client && !currentUser) {
          openAuthModal();
          return;
      }

      setCurrentPage(targetPage);
      setShowPremiumModal(false);
      setShowLegalModal(false);
      setShowProfileModal(false);
      setShowAdminLogin(false);
      setProfileMedium(null);
      if (targetPage === 'messages') {
          setInitialMessageMedium(null);
      }
      navigateTo(`/${targetPage === 'home' ? '' : targetPage}`, true);
  };

  const handleOpenSarah = () => {
      setIsSarahFloatingDismissed(false);
      setPurchasePack(null);
      setPendingVipMedium(null);
      setShowPurchaseChat(true);
      navigateTo('/sarah', true);
  };

  const renderPage = () => {
      switch(currentPage) {
          case 'home':
              return (
                  <Home 
                    onNavigate={handleNavigate} 
                    onChatStart={handleChatStart}
                    onProfileClick={handleProfileClick}
                    onOpenPremium={handleOpenPurchase}
                    onOpenProfile={() => handleNavigate('profile')}
                    guestUser={client ? {name: client.name, phone: client.phone} : guestUser} 
                  />
              );
          case 'mediums':
              return (
                <MediumsList 
                    onChatStart={handleChatStart} 
                    onProfileClick={handleProfileClick} 
                    initialCategory={selectedCategory}
                />
              );
          case 'horoscope':
              return <Horoscope />;
          case 'crystal':
              return <CrystalBall />;
          case 'love':
              return <LoveCompatibility />;
          case 'numerology':
              return <Numerology />;
          case 'dreams':
              return <DreamInterpretation />;
          case 'tarot':
              return <TarotOracle />;
          case 'forum':
              return <Forum />;
          case 'tarifs':
          case 'contact':
          case 'services':
              return <Services onOpenPremium={handleOpenPurchase} onNavigate={handleNavigate} />;
          case 'messages':
              return (
                <MessagesPage 
                  onNavigate={handleNavigate}
                  onOpenSarah={handleOpenSarah}
                  guestUser={client ? {name: client.name, phone: client.phone} : guestUser}
                  initialMedium={initialMessageMedium}
                  onClearInitialMedium={() => setInitialMessageMedium(null)}
                />
              );
          case 'profile':
              return (
                <ClientProfilePage 
                  onNavigate={handleNavigate}
                  onChatStart={handleChatStart}
                  onOpenSarah={handleOpenSarah}
                  onOpenPremium={() => handleOpenPurchase('50')}
                  onClose={() => handleNavigate('home')}
                />
              );
          case 'menu':
              return (
                <MobileMenuPage 
                  onNavigate={handleNavigate}
                  onOpenSarah={handleOpenSarah}
                  onOpenAuth={() => openAuthModal('login')}
                  onOpenProfile={() => handleNavigate('profile')}
                />
              );
          case 'ressources':
              return (
                <ResourcesPage 
                  onNavigate={handleNavigate} 
                />
              );
          default:
              return (
                  <Home 
                    onNavigate={handleNavigate} 
                    onChatStart={handleChatStart}
                    onProfileClick={handleProfileClick}
                    onOpenPremium={handleOpenPurchase}
                    onOpenProfile={() => handleNavigate('profile')}
                    guestUser={client ? {name: client.name, phone: client.phone} : guestUser}
                  />
              );
      }
  };

  if (isAuthenticated) {
      return (
          <ErrorBoundary>
              <Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">Chargement du Dashboard...</div>}>
                  <AdminDashboard onClose={() => { logout(); navigateTo('/', true); }} />
              </Suspense>
          </ErrorBoundary>
      );
  }

  return (
    <div className="min-h-[100dvh] w-full bg-transparent font-sans selection:bg-purple-500 selection:text-white flex flex-col relative">
      {/* POPUP BLOQUANTE OBLIGATOIRE : EXIGE AU VISITEUR DE CRÉER UN COMPTE OU DE SE CONNECTER */}
      <MandatoryLeadModal 
        isOpen={isLeadModalVisible}
        onSuccess={(profile) => {
          setIsLeadDone(true);
          try {
            localStorage.setItem('astro_user_registered_v2', 'true');
          } catch (e) {}

          if (profile.isLogin) {
            // Client authentifié avec succès via le mode connexion
            return;
          }
          try {
            const fullName = `${profile.prenom} ${profile.nom}`.trim();
            if (typeof quickRegisterClient === 'function') {
              quickRegisterClient(fullName, profile.phone, profile.email, profile.address, profile.password);
            }
            setGuestUser((prev: any) => ({
              ...(prev || {}),
              name: fullName,
              phone: profile.phone,
              email: profile.email,
              address: profile.address
            }));
          } catch (err) {}
        }}
      />

      <MysticBackground />
      <CookieConsent />
      <IOSPwaPrompt /> 
      
      <ClientAuthModal /> 
      
      {showAdminLogin && (
          <AdminLogin onClose={() => { setShowAdminLogin(false); navigateTo(`/${currentPage === 'home' ? '' : currentPage}`, true); }} />
      )}

      {showProfileModal && (
          <ClientProfileModal 
              onClose={() => { 
                  setShowProfileModal(false); 
                  if (currentPage === 'profile') {
                      handleNavigate('home');
                  } else {
                      navigateTo(`/${currentPage === 'home' ? '' : currentPage}`, true);
                  }
              }} 
              onChatStart={handleChatStart}
              onSelectMedium={(m) => handleProfileClick(m)}
          />
      )}

      <LiveVoiceCallModal />

      <PromoNotificationWidget 
          onOpenPremium={(pack) => handleOpenPurchase(pack)}
          onOpenSarah={handleOpenSarah}
          onOpenTarot={() => handleNavigate('tarot')}
      />
      
      {currentPage === 'home' && (
          <LiveActivityFeed 
              userLocation={userLocation}
              onProfileClick={handleProfileClick}
              onOpenPremium={handleOpenPurchase}
          />
      )}
      
            
      {profileMedium && (
          <MediumProfileModal 
            medium={profileMedium} 
            onClose={() => { setProfileMedium(null); navigateTo(`/${currentPage === 'home' ? '' : currentPage}`, true); }} 
            onChatClick={handleChatStart}
          />
      )}


      
      {showNightChat && (
          <NightSupportChat onClose={() => { setShowNightChat(false); navigateTo(`/${currentPage === 'home' ? '' : currentPage}`, false); }} />
      )}
      
      {showPremiumModal && (
          <PremiumModal 
            initialPack={purchasePack}
            onClose={() => { setShowPremiumModal(false); navigateTo(`/${currentPage === 'home' ? '' : currentPage}`, false); }} 
            onProceedToPurchase={(pack) => handleOpenPurchase(pack)}
          />
      )}

      {showLegalModal && (
          <LegalModal onClose={() => { setShowLegalModal(false); navigateTo(`/${currentPage === 'home' ? '' : currentPage}`, false); }} />
      )}
      
      <Header 
        currentPage={currentPage} 
        onNavigate={handleNavigate} 
        unreadMessagesCount={unreadMessagesCount} 
        onOpenProfile={() => { setShowProfileModal(true); navigateTo('/client/profile', true); }}
        onOpenSarah={handleOpenSarah}
        onRecharge={() => {
            setIsSarahFloatingDismissed(false);
            setShowPurchaseChat(true);
            navigateTo('/tarifs', true);
        }}
      />

      <main className="flex-grow relative">
        <ErrorBoundary>
            {renderPage()}
        </ErrorBoundary>
      </main>
      
      {/* CHAT SECRÉTARIAT (SARAH) — ACCESSIBLE À TOUT LE MONDE (INSCRIT OU NON, MOBILE ET DESKTOP) */}
      {showPurchaseChat && (
          <div className="fixed inset-0 md:inset-auto md:bottom-0 md:right-0 z-[150] p-2 md:p-3 flex items-end justify-center md:justify-end pointer-events-none bg-black/60 md:bg-transparent backdrop-blur-xs md:backdrop-blur-none">
              <div className="pointer-events-auto shrink-0 max-w-full">
                  <AdminPurchaseChat 
                    initialPack={purchasePack}
                    vipMediumName={pendingVipMedium?.name || null}
                    onClose={() => {
                        setShowPurchaseChat(false);
                        setPendingVipMedium(null);
                    }} 
                    onConsultStart={() => {
                        setShowPurchaseChat(false);
                        setPendingVipMedium(null);
                        setCurrentPage('mediums');
                    }}
                  />
              </div>
          </div>
      )}

      {/* Container for standard Medium LiveChats (Desktop only & Outside messages page) */}
      {currentPage !== 'messages' && (
      <div className="fixed bottom-0 right-0 z-50 p-2 hidden md:flex md:flex-row-reverse items-end gap-3 pointer-events-none max-w-full">
          {/* Standard Medium LiveChats */}
          {chatSessions.map((session) => (
              <div key={session.mediumId} className="pointer-events-auto shrink-0">
                  <LiveChat 
                    currentMedium={session.medium} 
                    guestUser={guestUser}
                    userLocation={userLocation}
                    onClose={() => handleCloseSession(session.mediumId)}
                  />
              </div>
          ))}
      </div>
      )}

      {/* BULLE DE CHAT FLOTTANTE DU SECRÉTARIAT (SARAH) — SEULEMENT APRÈS VALIDATION DES COOKIES */}
      {!isAuthenticated && currentPage !== 'messages' && chatSessions.length === 0 && !showPurchaseChat && !isSarahFloatingDismissed && hasCookieConsent && (
          <div className="fixed bottom-[5.5rem] right-3.5 sm:bottom-6 sm:right-6 z-[140] flex items-center gap-2 pointer-events-auto">
              <div className="relative group flex items-center">
                  {/* Bouton de fermeture / masquage de la bulle flottante */}
                  <button
                      onClick={(e) => {
                          e.stopPropagation();
                          setIsSarahFloatingDismissed(true);
                      }}
                      className="absolute -top-1 -left-1 z-30 w-5 h-5 bg-black/90 hover:bg-red-600 border border-white/30 text-gray-300 hover:text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow-md transition-all cursor-pointer"
                      title="Masquer l'assistance"
                      aria-label="Masquer l'assistance"><XIcon className="w-2.5 h-2.5" /></button>

                  {/* Bouton Bulle Flottante Haute Élégance */}
                  <button
                      onClick={() => {
                          setHasUnreadSarahBubble(false);
                          setPurchasePack(null);
                          setPendingVipMedium(null);
                          setShowPurchaseChat(true);
                      }}
                      className="relative flex items-center gap-2 p-1.5 pr-3.5 sm:pr-4 bg-gradient-to-r from-amber-500/30 via-[#160B2E]/95 to-black/95 backdrop-blur-xl border border-amber-400/60 hover:border-amber-300 rounded-full shadow-[0_0_25px_rgba(245,158,11,0.45)] hover:shadow-[0_0_35px_rgba(245,158,11,0.7)] hover:scale-105 active:scale-95 transition-all duration-300 cursor-pointer"
                      title="Discuter avec le Secrétariat Sarah en direct"
                  >
                      <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-amber-400 shrink-0 bg-mystic-900 shadow-md">
                          <img 
                              src="/sarah.jpg" 
                              alt="Sarah" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                  (e.target as HTMLElement).style.display = 'none';
                              }}
                          />
                          <span className="w-full h-full flex items-center justify-center bg-purple-950"><UserCheckIcon className="w-6 h-6 text-pink-300" /></span>
                          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-400 border-2 border-black rounded-full shadow animate-pulse"></span>
                      </div>

                      <div className="flex flex-col text-left leading-tight pr-1">
                          <span className="text-xs font-bold text-white group-hover:text-amber-300 transition-colors flex items-center gap-1">
                              <span>Secrétariat Sarah</span>
                              <span className="hidden sm:inline-block text-[9px] bg-gold-500/20 text-gold-300 px-1 rounded border border-gold-500/30">Direct</span>
                          </span>
                          <span className="text-[10px] text-amber-300/90 font-medium flex items-center gap-1">
                              <span>En direct</span>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                          </span>
                      </div>

                      {/* PASTILLE NOTIFICATION ROUGE ANIMÉE */}
                      {hasUnreadSarahBubble && (
                          <span className="absolute -top-1 -right-1 flex h-5 w-5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-5 w-5 bg-red-600 text-white font-black text-[10px] items-center justify-center border-2 border-white shadow-lg">
                                  1
                              </span>
                          </span>
                      )}
                  </button>
              </div>
          </div>
      )}

      {/* WIDGET APPEL FLOTTANT SUPPRIMÉ POUR LES CLIENTS */}

      {currentPage !== 'messages' && (
        <Footer 
          onNavigate={handleNavigate} 
          onOpenLegal={() => setShowLegalModal(true)} 
          onOpenSarah={() => {
              setIsSarahFloatingDismissed(false);
              setPurchasePack(null);
              setPendingVipMedium(null);
              setShowPurchaseChat(true);
          }}
        />
      )}

      {/* Mobile Fixed Bottom Navigation (Always accessible) */}
      <MobileBottomNav 
        currentPage={currentPage} 
        onNavigate={handleNavigate} 
        unreadMessagesCount={unreadMessagesCount}
      />
    </div>
  );
};

function App() {
    return (
        <AdminProvider>
            <ChatLayoutProvider>
                <AppContent />
            </ChatLayoutProvider>
        </AdminProvider>
    );
}

export default App;
